import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Light haptic feedback at key moments (link saved, pull-to-refresh armed,
 * favourite, selection…). One call site API: `haptic('light' | 'medium' |
 * 'success' | 'warning' | 'error')`, always fire-and-forget.
 *
 *  - Native apps: the @capacitor/haptics plugin (Taptic Engine on iPhone),
 *    reached through registerPlugin like RevenueCat so the web build never
 *    imports native code. Builds made before the plugin was added simply
 *    report it unavailable, and nothing happens.
 *  - Web: the Vibration API, i.e. Android browsers. iOS Safari has none.
 *
 * The user can turn it off in Settings (stored per device).
 */
const Haptics = registerPlugin('Haptics');
const KEY = 'tofbox_haptics';

const PATTERNS = {
  light: 8, medium: 16, success: [10, 60, 16], warning: [20, 80, 20], error: [30, 60, 30, 60, 30],
};

const nativeAvailable = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Haptics');

export const hapticsSupported = () =>
  nativeAvailable() || (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function');

export const hapticsEnabled = () => {
  try { return localStorage.getItem(KEY) !== 'off'; } catch { return true; }
};

export const setHapticsEnabled = (on) => {
  try { localStorage.setItem(KEY, on ? 'on' : 'off'); } catch { /* private mode */ }
};

export function haptic(kind = 'light') {
  if (!hapticsEnabled()) return;
  try {
    if (nativeAvailable()) {
      const call = kind === 'success' || kind === 'warning' || kind === 'error'
        ? Haptics.notification({ type: kind.toUpperCase() })
        : Haptics.impact({ style: kind === 'medium' ? 'MEDIUM' : 'LIGHT' });
      call.catch(() => {});
      return;
    }
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(PATTERNS[kind] || PATTERNS.light);
    }
  } catch { /* never let feedback break an action */ }
}
